/**
 * Mischt mehrere Tonquellen eines Geraets zu *einer* Spur.
 *
 * Gebraucht wird das genau bei der Bildschirmuebertragung. `getDisplayMedia`
 * liefert das Bild und, wenn der Browser mitspielt, den Ton des geteilten
 * Inhalts -- aber nicht das Mikrofon. Wer eine Folie zeigt und dazu redet,
 * geht damit stumm raus; wer ein Video zeigt, will beides. Also zwei
 * Eingaenge: Bild vom Bildschirm, Ton von Bildschirm *und* Mikrofon.
 *
 * Zwei Tonspuren einfach in denselben MediaStream zu legen, hilft dabei
 * nicht: ein `<video>` spielt genau eine davon, und auch der RTMP-Ausgang
 * nimmt am Ende nur eine. Deshalb laufen sie hier durch einen Web-Audio-Graph
 * zusammen und verlassen ihn als eine Spur, die aussieht wie ein ganz
 * normales Mikrofon.
 *
 * Geschaltet wird *nicht* hier, sondern an den Eingangsspuren selbst
 * (`track.enabled`): eine abgeschaltete Spur liefert Stille in den Mischer,
 * und das ist dasselbe Ergebnis wie ein Pegel auf null -- nur ohne dass der
 * Mischer wissen muss, was die Regie gerade will.
 */

export type MixLane = 'mic' | 'system';

/**
 * Die feste Mischung. Der Kommentar gehoert nach vorne, der Ton des geteilten
 * Fensters darunter -- wer eine Aufzeichnung zeigt und dazu spricht, will
 * gehoert werden. Regler dafuer gibt es bewusst nicht: waehrend einer Sendung
 * an zwei Pegeln zu drehen ist ein Handgriff mehr, als eine Regie hat.
 */
const LANE_GAIN: Record<MixLane, number> = { mic: 1, system: 0.6 };

export class AudioMix {
  private lanes = new Map<MixLane, { source: MediaStreamAudioSourceNode; gain: GainNode }>();

  private constructor(
    private ctx: AudioContext,
    private dest: MediaStreamAudioDestinationNode,
    /**
     * Bremse gegen Uebersteuern. Zwei laute Quellen addieren sich, und alles
     * ueber Vollpegel wird beim Kodieren zu Knacken -- hoerbar genau dann,
     * wenn jemand ueber eine laute Stelle spricht. Der Kompressor greift erst
     * kurz unter Vollpegel ein und laesst alles darunter unberuehrt.
     */
    private bus: DynamicsCompressorNode,
  ) {}

  /**
   * Baut den Mischer auf. Gibt `null` zurueck, wenn der Browser kein Web
   * Audio hat -- dann faellt der Aufrufer auf eine einzelne Tonspur zurueck,
   * statt ganz ohne Ton zu senden.
   */
  static create(): AudioMix | null {
    const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    const ctx = new Ctor({ sampleRate: 48_000 });
    // Der Aufruf kommt aus einem Klick, der Kontext laeuft also meist schon.
    // Ein resume kostet nichts und rettet den Fall, in dem er es nicht tut.
    void ctx.resume().catch(() => undefined);
    const dest = ctx.createMediaStreamDestination();

    /*
     * Stille Dauerquelle -- dieselbe Falle wie im Renderer (tabRecorder.ts):
     * solange nichts angeschlossen ist, liefert die Zielspur ueberhaupt keine
     * Samples. Eine WebRTC-Verbindung wuerde dann eine Tonspur aushandeln,
     * durch die nie etwas kommt, und der Empfaenger bekaeme auch spaeter
     * nichts mehr zu hoeren. Pegel 0 heisst: hoerbar ist nichts, vorhanden
     * ist die Spur trotzdem.
     */
    const silence = ctx.createConstantSource();
    silence.offset.value = 0;
    silence.connect(dest);
    silence.start();

    const bus = ctx.createDynamicsCompressor();
    bus.threshold.value = -6;
    bus.knee.value = 6;
    bus.ratio.value = 12;
    bus.attack.value = 0.003;
    bus.release.value = 0.25;
    bus.connect(dest);

    return new AudioMix(ctx, dest, bus);
  }

  /** Haengt eine Tonquelle in den Mischer, `null` nimmt sie wieder heraus. */
  set(lane: MixLane, stream: MediaStream | null) {
    const existing = this.lanes.get(lane);
    if (existing) {
      existing.source.disconnect();
      existing.gain.disconnect();
      this.lanes.delete(lane);
    }
    if (!stream || stream.getAudioTracks().length === 0) return;
    try {
      // Nur die Tonspuren: ein Stream mit Bild darin waere hier ein Fehler.
      const source = this.ctx.createMediaStreamSource(new MediaStream(stream.getAudioTracks()));
      const gain = this.ctx.createGain();
      gain.gain.value = LANE_GAIN[lane];
      source.connect(gain);
      gain.connect(this.bus);
      this.lanes.set(lane, { source, gain });
    } catch (err) {
      console.warn('[audio] Quelle konnte nicht gemischt werden', lane, err);
    }
  }

  /** Die gemischte Spur -- genau die geht in die WebRTC-Verbindung. */
  get track(): MediaStreamTrack | null {
    return this.dest.stream.getAudioTracks()[0] ?? null;
  }

  close() {
    for (const { source, gain } of this.lanes.values()) {
      source.disconnect();
      gain.disconnect();
    }
    this.lanes.clear();
    this.bus.disconnect();
    void this.ctx.close().catch(() => undefined);
  }
}
